import { type HTMLAttributes, forwardRef, memo, ReactNode, useEffect, useImperativeHandle, useState } from 'react';

import { BaseEngineOptions, EngineConstructor, ModuleConstructor } from '../injectable';
import { Zed } from '../Zed';
import { ZedBuilder } from '../ZedBuilder';

import { ZedContext } from './useZed';
import { ZedCanvas } from './ZedCanvas';

type ZedProps = Omit<HTMLAttributes<HTMLDivElement>, 'width' | 'height'> & {
  engine: EngineConstructor;
  engineOptions?: BaseEngineOptions;
  modules?: ModuleConstructor[];
  values?: Record<symbol | string, any>;
  children?: ReactNode;
};

const _ZedComponent = forwardRef(function Zed(
  { className, style, engine, engineOptions, modules, values = {}, children, ...rest }: ZedProps,
  ref
) {
  const [zed, setZed] = useState<Zed<{}>>();

  useImperativeHandle(ref, () => zed, [zed]);

  useEffect(() => {
    const builder = new ZedBuilder(engine, {
      ...engineOptions,
    });

    modules?.forEach((m) => {
      builder.addModule(m);
    });

    Reflect.ownKeys(values).forEach((token) => {
      builder.addValue(token, values[token]);
    });

    const zed = builder.build();

    setZed(zed);

    return () => {
      zed.dispose();
    };
  }, [engine, modules, values]);

  return (
    <div className={className} style={style} {...rest}>
      <ZedContext.Provider value={zed}>
        <ZedCanvas
          style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
          }}
          options={{
            input: true,
          }}
          touch-action="none"
        />
        {children}
      </ZedContext.Provider>
    </div>
  );
});

export const ZedComponent = memo(_ZedComponent);
export default ZedComponent;
